import type { FileMeta } from '~/hooks/queries/use-files';
import { uploadHasTextBody } from '~/features/chat/modal-asset';

/**
 * FRONT-1642: whether a file search hit needs the command palette's file
 * preview to show TWO separate "Open …" actions instead of the ONE it has
 * always shown.
 *
 * True only when the hit carries BOTH an extracted-text body
 * (`uploadHasTextBody(kind)`) AND original bytes (`hasBytes`) — a PDF today.
 * That is the ONLY case this ticket touches. Every other combination — a
 * plain text/markdown hit (no bytes), an image hit (bytes, no extracted
 * text), or a binary hit (bytes; it may carry extracted text once healed,
 * FRONT-2365 round 4 — but `uploadHasTextBody` only reports `'text'`/`'pdf'`
 * kinds true, so a binary hit never satisfies this function regardless) —
 * keeps the pre-existing single "Open file" action completely unchanged:
 * same label, same `<button>` (never an anchor), same navigate-to-`/files/:id`
 * behaviour, same ↵ badge.
 *
 * FRONT-1607 (image OCR) is NOT a free win for this function on its own:
 * once it lands, an image upload could carry `hasBytes: true` alongside
 * real extracted text — but `uploadHasTextBody` (imported above, defined in
 * `~/features/chat/modal-asset`) hardcodes `kind === 'text' || kind ===
 * 'pdf'` and returns `false` for `'image'` today. An image hit will only
 * ever satisfy this function, and split, once THAT is also updated to
 * include `'image'` — a second, deliberate change this ticket does not
 * make. (An earlier version of this comment implied the split would follow
 * automatically "once FRONT-1607 lands" — corrected on review, since it
 * doesn't.)
 *
 * A first version of this returned two independent booleans and
 * relabelled/restyled the single-action case too (e.g. "Open text" for a
 * plain text hit, a raw-bytes anchor for an image/binary hit) — narrowed
 * back to this one question on review, because neither of those was part of
 * the request and both widened the change's blast radius onto file kinds
 * nobody asked to touch (an image/binary hit's single action used to land on
 * the richer in-app detail page — name, size, description, delete, the
 * inline image or a download link — which a raw-bytes anchor would have
 * silently replaced with a bare new-tab download).
 *
 * Pure function of the two fields a search hit already carries
 * (`FileMeta.kind`/`FileMeta.hasBytes`), not a `FileMeta` itself, so a
 * caller — or a test — can exercise combinations the backend doesn't
 * produce today. `kind === 'text' ⇔ !hasBytes` always holds server-side, but
 * this function makes no assumption that invariant holds; it is a straight
 * function of whatever two values it's handed.
 */
export function shouldSplitFileActions(
  kind: FileMeta['kind'],
  hasBytes: boolean,
): boolean {
  return hasBytes && uploadHasTextBody(kind);
}
